import * as React from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useAppDispatch } from "@/store/hooks";
import { setReturnUrl } from "@/store/slices/authSlice";
import { cn } from "@/lib/utils";
import {
  ProfileIcon,
  ClothesIcon,
  PairingIcon,
  SupportIcon,
  ContactIcon,
} from "./icons";

export interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  onClick?: () => void;
}

export interface MobileMenuProps {
  /**
   * Whether the menu is open or closed
   */
  isOpen: boolean;
  /**
   * Callback when the menu should close
   */
  onClose: () => void;
  /**
   * Optional custom menu items (defaults to standard ANYW3AR menu)
   */
  items?: MenuItem[];
  /**
   * Optional callback when a menu item is selected (receives item id)
   */
  onNavigate?: (itemId: string) => void;
  /**
   * Whether the user is authenticated (for auth-aware navigation)
   */
  isAuthenticated?: boolean;
  /**
   * Additional className for the root element
   */
  className?: string;
  /**
   * Animation delay between menu items appearing (in ms)
   * @default 150
   */
  animationDelay?: number;
}

/**
 * MobileMenu - Full-screen animated mobile navigation menu
 *
 * Features:
 * - Full-screen overlay with semi-transparent red background
 * - Staggered entrance animation for menu items
 * - Auto-close on item selection
 * - Support for both navigation and custom callbacks
 * - Footer with copyright and legal links
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * <MobileMenu
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onNavigate={(itemId) => console.log('Navigate to:', itemId)}
 * />
 * ```
 */
export const MobileMenu = React.forwardRef<HTMLDivElement, MobileMenuProps>(
  (
    {
      isOpen,
      onClose,
      items,
      onNavigate,
      isAuthenticated,
      className,
      animationDelay = 150,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [animationStage, setAnimationStage] = React.useState(0);
    const [isClosing, setIsClosing] = React.useState(false);
    const [isEntering, setIsEntering] = React.useState(true);
    const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(
      null
    );
    const prevIsOpenRef = React.useRef(isOpen);

    // Reset state BEFORE paint when menu opens (useLayoutEffect runs before paint)
    React.useLayoutEffect(() => {
      if (isOpen && !prevIsOpenRef.current) {
        // Menu just opened - force reset ALL animation state before browser paints
        setIsEntering(true);
        setAnimationStage(0);
        setIsClosing(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
      prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    // Default ANYW3AR menu items (updated routes per Figma)
    const defaultItems: MenuItem[] = [
      {
        id: "profile",
        label: "My profile",
        icon: <ProfileIcon size={16} weight="regular" />,
        href: "/owner/profile",
      },
      {
        id: "collection",
        label: "My collection",
        icon: <ClothesIcon size={16} />,
        href: "/owner/trophy-room",
      },
      {
        id: "scan",
        label: "Scan a new item",
        icon: <PairingIcon size={16} />,
        href: "/pairing-help",
      },
      {
        id: "support",
        label: "Support",
        icon: <SupportIcon size={16} weight="regular" />,
        href: "/support",
      },
      {
        id: "contact",
        label: "Contact",
        icon: <ContactIcon size={16} />,
        href: "/contact",
      },
    ];

    const menuItems = items || defaultItems;

    // Handle opening animation sequence
    React.useEffect(() => {
      if (isOpen && !isClosing) {
        // Set entering state to skip transitions during reset
        setIsEntering(true);
        // Reset animation stage
        setAnimationStage(0);

        // Start animation sequence
        const timer = setTimeout(() => {
          // Clear entering state after first tick - transitions now allowed
          setIsEntering(false);

          intervalRef.current = setInterval(() => {
            setAnimationStage((prev) => {
              if (prev >= menuItems.length) {
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                  intervalRef.current = null;
                }
                return prev;
              }
              return prev + 1;
            });
          }, animationDelay);
        }, 100); // Small delay for overlay to appear

        return () => {
          clearTimeout(timer);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        };
      }
    }, [isOpen, isClosing, menuItems.length, animationDelay]);

    // Handle closing animation
    const handleClose = React.useCallback(() => {
      // Clear any running animation interval immediately
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      setIsClosing(true);
      setAnimationStage(0);

      setTimeout(() => {
        onClose();
        setIsClosing(false);
        setIsEntering(true); // Reset for next open - prevents flash
      }, 400); // Fade-out with visible animation
    }, [onClose]);

    // Handle item click with authentication-aware navigation
    const handleItemClick = React.useCallback(
      (item: MenuItem) => {
        // Call the item's onClick if provided
        if (item.onClick) {
          item.onClick();
          handleClose();
          return;
        }

        // Handle navigation with authentication check
        if (item.href) {
          if (
            isAuthenticated === false &&
            item.href !== "/support" &&
            item.href !== "/contact"
          ) {
            // Unauthenticated user trying to access protected route
            dispatch(setReturnUrl(item.href));

            // Close menu immediately (provides closure)
            handleClose();

            // Show informative toast (clear feedback)
            toast.info(`Please log in to access "${item.label}"`);

            // Navigate only if not already on login page
            const currentPath = window.location.pathname;
            if (currentPath !== "/auth/login") {
              navigate("/auth/login");
            }
          } else {
            // Authenticated user or public route
            navigate(item.href);
            handleClose(); // Close menu after navigation
          }
        }

        // Call the onNavigate callback if provided
        if (onNavigate) {
          onNavigate(item.id);
        }
      },
      [isAuthenticated, navigate, dispatch, onNavigate, handleClose]
    );

    if (!isOpen && !isClosing) {
      return null;
    }

    return (
      <>
        {/* Keyframe animation for stepped group slide */}
        <style>{`
          @keyframes groupSlideSteps {
            0% {
              transform: translateX(3rem) translateY(1rem);
            }
            30% {
              transform: translateX(2rem) translateY(1rem);
            }
            60% {
              transform: translateX(1rem) translateY(1rem);
            }
            80% {
              transform: translateX(0) translateY(1rem);
            }
            100% {
              transform: translateX(0) translateY(0);
            }
          }
        `}</style>

        <div
          ref={ref}
          className={cn("fixed inset-0 z-30 flex flex-col", className)}
          style={{
            opacity: isOpen && !isClosing && !isEntering ? 1 : 0,
            transition: isClosing
              ? "opacity 400ms ease-out"
              : isEntering
              ? "none"
              : "opacity 400ms ease-in-out",
          }}
        >
          {/* Semi-transparent red backdrop */}
          <div
            className="absolute inset-0 bg-brand-red/80"
            onClick={handleClose}
            aria-label="Close menu"
          />

          {/* Menu content */}
          <div className="relative flex h-full flex-col">
            {/* Menu items container */}
            <div className="flex flex-1 flex-col items-center justify-center gap-8 px-8 py-16">
              <nav
                className="flex flex-col gap-4"
                style={{
                  transform: isClosing
                    ? "translateX(3rem) translateY(1rem)"
                    : "translateX(0) translateY(0)",
                  animation:
                    !isClosing && animationStage > 0
                      ? "groupSlideSteps 700ms ease-in-out forwards"
                      : "none",
                  transition: isClosing ? "transform 400ms ease-out" : "none",
                }}
              >
                {menuItems.map((item, index) => {
                  const isVisible = animationStage > index;
                  const delay = index * 50; // Slight delay for transform animation

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className={cn(
                        "flex items-center gap-[0.625rem] px-4 py-2",
                        "text-white hover:bg-white/10 rounded-lg",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                      )}
                      style={{
                        opacity: isClosing ? 0 : isVisible ? 1 : 0,
                        transition: isClosing
                          ? "opacity 400ms ease-out"
                          : isEntering
                          ? "none"
                          : `opacity 250ms ease-out ${delay}ms`,
                      }}
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        {item.icon}
                      </span>
                      <span className="font-montserrat text-[1.25rem] font-medium">
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Footer */}
            <div
              className={cn(
                "flex items-center justify-center gap-1 px-8 py-3",
                "text-xs text-white"
              )}
              style={{
                opacity:
                  animationStage >= menuItems.length && !isClosing ? 1 : 0,
                transition: isClosing
                  ? "opacity 400ms ease-out"
                  : isEntering
                  ? "none"
                  : "opacity 400ms ease-in-out",
              }}
            >
              <span className="font-inter">ANYW3AR © - 2026 -</span>
              <a
                href="https://anyw3ar.com/pages/terms"
                className="font-inter underline underline-offset-2 hover:text-white/80"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
              >
                Legals
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }
);

MobileMenu.displayName = "MobileMenu";
